import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowLeft, Plus, Trash2, RotateCcw } from 'lucide-react';
import { motion } from 'motion/react';

interface StickyNote {
  id: string;
  content: string;
  author: string;
  color: string;
  position: { x: number; y: number };
  avatar: string;
}

const colors = [
  'bg-yellow-100',
  'bg-pink-100',
  'bg-blue-100',
  'bg-green-100',
  'bg-purple-100',
  'bg-orange-100',
];

const avatarColors = [
  'bg-blue-500',
  'bg-green-500',
  'bg-purple-500',
  'bg-orange-500',
  'bg-pink-500',
  'bg-teal-500',
];

const demoStudents = ['Alex', 'Jordan', 'Sam', 'Taylor', 'Morgan', 'Casey'];

function DraggableStickyNote({
  note,
  onDelete,
  onMove,
}: {
  note: StickyNote;
  onDelete: (id: string) => void;
  onMove: (id: string, position: { x: number; y: number }) => void;
}) {
  const [{ isDragging }, drag] = useDrag({
    type: 'NOTE',
    item: { id: note.id, startX: note.position.x, startY: note.position.y },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    end: (item, monitor) => {
      const delta = monitor.getDifferenceFromInitialOffset();
      if (delta) {
        onMove(note.id, {
          x: Math.max(0, item.startX + delta.x),
          y: Math.max(0, item.startY + delta.y),
        });
      }
    },
  });

  return (
    <motion.div
      ref={drag as any}
      initial={{ scale: 0, rotate: -10 }}
      animate={{ scale: 1, rotate: 0 }}
      style={{
        position: 'absolute',
        left: note.position.x,
        top: note.position.y,
        opacity: isDragging ? 0.5 : 1,
      }}
      className="cursor-move group"
    >
      <div
        className={`w-56 p-4 rounded-2xl shadow-lg border-2 border-border hover:shadow-xl transition-shadow ${note.color}`}
      >
        <div className="flex items-center gap-2 mb-3">
          <div className={`w-8 h-8 rounded-full ${note.avatar} flex items-center justify-center text-white text-sm font-semibold`}>
            {note.author.split(' ').map(n => n[0]).join('')}
          </div>
          <span className="font-medium text-sm flex-1 truncate">{note.author}</span>
          <button
            onClick={() => onDelete(note.id)}
            className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
            aria-label={`Delete note by ${note.author}`}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
        <p className="text-foreground leading-relaxed text-sm">{note.content}</p>
      </div>
    </motion.div>
  );
}

function BoardDropZone({
  children,
  onDropNote,
}: {
  children: React.ReactNode;
  onDropNote: (id: string, position: { x: number; y: number }) => void;
}) {
  const [, drop] = useDrop({
    accept: 'NOTE',
    drop: () => undefined,
  });

  return (
    <div ref={drop as any} className="relative w-full h-full">
      {children}
    </div>
  );
}

export default function DemoCollaborateBoard() {
  const navigate = useNavigate();
  const [notes, setNotes] = useState<StickyNote[]>([
    {
      id: '1',
      content: 'Great idea for our project! We should focus on sustainability.',
      author: 'Alex',
      color: 'bg-yellow-100',
      position: { x: 50, y: 80 },
      avatar: 'bg-blue-500',
    },
    {
      id: '2',
      content: 'What about using renewable energy sources?',
      author: 'Jordan',
      color: 'bg-pink-100',
      position: { x: 320, y: 120 },
      avatar: 'bg-green-500',
    },
    {
      id: '3',
      content: 'We need to consider the budget constraints.',
      author: 'Sam',
      color: 'bg-blue-100',
      position: { x: 580, y: 60 },
      avatar: 'bg-purple-500',
    },
  ]);
  const [newNoteContent, setNewNoteContent] = useState('');
  const [selectedStudent, setSelectedStudent] = useState(demoStudents[0]);

  const addNote = useCallback(() => {
    if (!newNoteContent.trim()) return;

    const newNote: StickyNote = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      content: newNoteContent.trim(),
      author: selectedStudent,
      color: colors[Math.floor(Math.random() * colors.length)],
      position: {
        x: Math.random() * 400 + 50,
        y: Math.random() * 250 + 50,
      },
      avatar: avatarColors[demoStudents.indexOf(selectedStudent) % avatarColors.length],
    };

    setNotes((prev) => [...prev, newNote]);
    setNewNoteContent('');
  }, [newNoteContent, selectedStudent]);

  const deleteNote = useCallback((id: string) => {
    setNotes((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const moveNote = useCallback((id: string, position: { x: number; y: number }) => {
    setNotes((prev) =>
      prev.map((n) => (n.id === id ? { ...n, position } : n))
    );
  }, []);

  const resetBoard = () => {
    setNotes([
      {
        id: '1',
        content: 'Great idea for our project! We should focus on sustainability.',
        author: 'Alex',
        color: 'bg-yellow-100',
        position: { x: 50, y: 80 },
        avatar: 'bg-blue-500',
      },
      {
        id: '2',
        content: 'What about using renewable energy sources?',
        author: 'Jordan',
        color: 'bg-pink-100',
        position: { x: 320, y: 120 },
        avatar: 'bg-green-500',
      },
      {
        id: '3',
        content: 'We need to consider the budget constraints.',
        author: 'Sam',
        color: 'bg-blue-100',
        position: { x: 580, y: 60 },
        avatar: 'bg-purple-500',
      },
    ]);
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-background to-emerald-50 dark:from-green-950/20 dark:via-background dark:to-emerald-950/20">
        {/* Header */}
        <header className="border-b border-border bg-card/80 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Button variant="ghost" onClick={() => navigate('/demos')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Demos
              </Button>
              <Button variant="outline" onClick={resetBoard}>
                <RotateCcw className="w-4 h-4 mr-2" />
                Reset Board
              </Button>
            </div>
          </div>
        </header>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold mb-2">Collaborate Board Demo</h1>
            <p className="text-muted-foreground">
              Add sticky notes and drag them around the board. Try different student names!
            </p>
          </div>

          {/* Add Note Section */}
          <div className="bg-card rounded-2xl p-4 border border-border mb-6 max-w-2xl mx-auto">
            <div className="flex flex-col sm:flex-row gap-3">
              <select
                value={selectedStudent}
                onChange={(e) => setSelectedStudent(e.target.value)}
                className="px-3 py-2 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {demoStudents.map((student) => (
                  <option key={student} value={student}>
                    {student}
                  </option>
                ))}
              </select>
              <Input
                placeholder="Write your idea..."
                value={newNoteContent}
                onChange={(e) => setNewNoteContent(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addNote()}
                className="flex-1 rounded-xl"
              />
              <Button onClick={addNote} className="rounded-xl" disabled={!newNoteContent.trim()}>
                <Plus className="w-4 h-4 mr-2" />
                Add Note
              </Button>
            </div>
          </div>

          {/* Board */}
          <div className="bg-card rounded-3xl border-2 border-border shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-green-100 to-emerald-100 dark:from-green-900/20 dark:to-emerald-900/20 px-6 py-4 border-b border-border">
              <h2 className="font-semibold text-lg">Brainstorming: Project Ideas</h2>
              <p className="text-sm text-muted-foreground">{notes.length} notes on the board</p>
            </div>
            <div className="relative h-[500px] bg-gradient-to-br from-white to-slate-50 dark:from-slate-950 dark:to-slate-900">
              {/* Grid pattern background */}
              <div
                className="absolute inset-0 opacity-30"
                style={{
                  backgroundImage: `
                    linear-gradient(to right, #e5e7eb 1px, transparent 1px),
                    linear-gradient(to bottom, #e5e7eb 1px, transparent 1px)
                  `,
                  backgroundSize: '40px 40px',
                }}
              />
              <BoardDropZone onDropNote={moveNote}>
                {notes.map((note) => (
                  <DraggableStickyNote
                    key={note.id}
                    note={note}
                    onDelete={deleteNote}
                    onMove={moveNote}
                  />
                ))}
              </BoardDropZone>
            </div>
          </div>

          {/* Instructions */}
          <div className="mt-6 text-center text-sm text-muted-foreground">
            <p>Tip: Drag notes to reposition them. Hover over a note to see the delete button.</p>
          </div>
        </div>
      </div>
    </DndProvider>
  );
}
